import {Headshot} from "../../../components/images";
import {GithubIcon, LinkedinIcon} from "lucide-react";
import {Button} from "../../../components/ui/button";
import {Link} from "react-router-dom";
import {Parallax} from "react-scroll-parallax";
import {cn} from "../../../lib/utils";
import {useEffect, useRef, useState, forwardRef, useImperativeHandle} from "react";
import {usePortfolioData} from "../data";
import thinkinAppsLogo from "../../../static/logo.png";

export type HomePageHeaderHandle = {
    scrollToContent: () => void;
};

const headshotThumb = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAhCAYAAAC4JqlRAAAFVElEQVR4AZTWO2uWSxAH8OERRVREUbwhingDEcQrogja2Nn5EewEay2sRLCys/MjiI2djYUoooidGpIUCSE3EhJyISEhyclvOfOyeZKTcMR/ZnZ2du6779PMz8+v1Lh79+4KXLt2bQUuXrzYofg26CScayP3alrbaKL1b2ZmZo1k586da9b1or3nLKROza8mGYncR9cFQMlGwhpyjaZjciCrwTG09xYWFqKNdQHMzs7Wtjbk24atazhkXVP84uJiAH7Hjh0BawL4/PlziZBCGshsyTZDZraZzkZ7awKgMD09jfwvcC4bqA+SQ8ravGqvC2D79u1Ff7PMHRwaGoo/f/7E6OhoTE1NlTP1n9pZW64NbNBZF8CpU6dq/TK5KXAIDhw4EHfu3IkHDx7ErVu34ubNm9Hf318Copvtw7extLTUaTO+BKD3r1+/jpcvX8aePXvCBDvIkCjxHKvOpUuX4vjx43H48OF4/Phx3Lhxo+Dp06cloImJicjqyRScr8FxrhuOnz17Fu/fvy8yzjkGAn0VhKzv3btHFD9+/IiPHz/Gu3fv4vv374W3IaDr169v2BL7sLy8jBTgG4ZkTTI2Noasg+xlPTIyEh8+fCjXR0BfvnyJnp6e+Pv3bzx//rwEpDKCZkTF0ESdecpKC2Sdgsw81+j+/fuR4uzEiRNx8uTJ0obbt28HPHnyJF68eFF0urq64syZMyHorcovoEbWnEKxsPpHyVdJ+c+Qnk5OThaju3fvjn379pU9fwSj7PDw4cO4f/9+DAwMxNGjR2Nubo5KB0reWfzLlApwYI0CPoNol9E+BwLSErpmAgWBaNevX79i7969RJuiBECDQ/c5K6GPZMpINjg4SK1cy/Hx8ejr6wu3xwwZxDdv3pQZoORmuKaCtG5DJZSfvFFijwrKWZsKQtZ1NqqiFSnj0HC+ffu23BCGtZYefjOUCpw9e7bTszyEgkowoArWBw8ejGPHjgWq/wZY2Q0h4FXHK2l4JeAsGyBJNNEwYlErWVMEvD2VwcsMzUHUDjPAMdhLm/itUCpASaTbtm3r/FzKFnJPyTMIWWd/PcFmQBB0QQXQGuzX6+QbmYBSA6eUZQ8Uk2qVtYqgqnH58uXyLFtnEHk72CHfDKUCXi+DttEBAWXm58+fLz8k9FTBmQwmnXieDeTp06fLu5HyjajEGs71LJ/jWpEjzjnhzCun5/V19eo575zS//z5M7yWjJPVyKtHpt1oqYCDMtICwoQ1MCiI7u7uyIO+AwTjUXIelN5MqJrg2amdWkPTFLfYaBySmYMGTeS+ihiwzuw4tOdUPiQq4Q1x/8m9B86kHtlWaHKaRa3c6JUrV8LAuesy40QmwCBKT1uGh4fLd0AG6ldSMioHgnUGjyYyyMZgAYcOX7hwoeh4ej21siTI0nNuzQBojzkicx2vXr1aAhIECNQe0K8pvrQAA/r87du38sbLTEDk/wX69j59+hSvXr0q3wraqRWPHj0qn23saCe9OphMpEwDA7IVMcUjR45EKmfm5Im2zDBqm29EdlSCrsqS4TMIPKSN5uvXr9Hb21t+4/VUmWRGQZTgAEqGT2SQ5ogzmQvefg43PtuKT7CHbzBZajwh4AWDT5AJgmPUr6Gp99ObQ+gGCcgZV/v379/lA8UPGFkbpQUM22CU06QckQM50DXRhw4dCoGrmqecjhuDGmCD6cPEGS3ybNO1X6MEQECRcU61ACVP5Joe3hXlwAsqY85V4dy5c+WrWAsE4TOeviuuWrt27er84LHdCYASAdS8dYIcTHbKUKXm0PC5tlrj98DXkn1r34eCUF2yxD8AAAD//1ga8/MAAAAGSURBVAMA76EKrbJMOogAAAAASUVORK5CYII=";

export const HomePageHeader = forwardRef<HomePageHeaderHandle>((props, ref) => {
    const data = usePortfolioData();
    const [showScrollHint, setShowScrollHint] = useState(true);

    const mainContainerRef = useRef<HTMLDivElement>(null);

    const scrollToContent = () => {
        setTimeout(() => {
            if (!mainContainerRef.current) {
                return;
            }
            window.scrollTo({
                top: mainContainerRef.current.scrollHeight,
                behavior: "smooth"
            });
        }, 100);
    }
    useImperativeHandle(ref, () => ({
        scrollToContent,
    }), []);

    useEffect(() => {
        function onScroll () {
            setShowScrollHint(window.scrollY < 20);
        }
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return <div ref={mainContainerRef}>
        <div className={"w-full flex flex-col items-center h-16 justify-center"}>
            <img alt={"logo"} src={thinkinAppsLogo} className={"flex flex-col items-center justify-center w-40 opacity-65 transition-all grayscale hover:grayscale-0 duration-500 ease-in-out hover:opacity-100 hover:scale-105 hover:drop-shadow-glow-secondary animate-in slide-in-from-top-1.5"}/>
        </div>
        <div className={"min-h-[calc(100svh-18rem)] max-w-[90vw] w-[100svw] flex flex-col"}>
            <div className={"flex flex-col items-center justify-center grow"}>
                <div className={"flex flex-row items-center flex-wrap justify-center gap-12"}>
                    <div style={{ backgroundImage: `url(${headshotThumb})`, backgroundSize: "cover" }} className={"w-72 max-w-[80vw] rounded-xl shadow-xl aspect-square overflow-hidden animate-in duration-1000 zoom-in-95"}>
                        <Parallax speed={-2}>
                            <Headshot className={"scale-115 hover:scale-[115%] transition-all duration-1000 w-full h-full"}/>
                        </Parallax>
                    </div>
                    <div className={"flex flex-col gap-4 max-w-[80vw]"}>
                        <div className={"group cursor-default"}>
                            <h1 className={"animate-in slide-in-from-bottom-1.5 duration-300"}>{ data.name }</h1>
                            <h4 className={"italic font-medium animate-in slide-in-from-bottom-2 duration-300"}>{ data.title }</h4>
                        </div>
                        <p className={"text-sm text-muted-foreground max-w-sm animate-in slide-in-from-bottom-2 duration-500"}>{ data.aboutMe }</p>
                        <ExternalProfileLinks/>
                    </div>
                </div>
            </div>
        </div>
        <ScrollHint onClick={scrollToContent} visible={showScrollHint}/>
    </div>
});

function ScrollHint(props: { visible: boolean, onClick?: () => void }) {
    return <div onClick={props.onClick}
        className={cn("text-sm opacity-45 flex flex-col items-center justify-center w-full h-[8rem] transition-all duration-700", !props.visible && "blur-xl opacity-0")}>
        Learn more about my...
    </div>
}

function ExternalProfileLinks() {
    const { links } = usePortfolioData();
    return <div className={"flex flex-row items-center gap-3"}>
        { links.linkedin && (
            <Link to={links.linkedin}>
                <Button variant={"outline"} size={"sm"}
                        className={"p-3 hover:bg-[#0a66c2] hover:border-transparent hover:text-white duration-300 animate-in slide-in-from-bottom-1.5"}>
                    <LinkedinIcon/>
                    LinkedIn
                </Button>
            </Link>
        )}
        { links.github && (
            <Link to={links.github}>
                <Button variant={"outline"} size={"sm"}
                        className={"p-3 hover:bg-black hover:border-transparent hover:text-white duration-300 animate-in slide-in-from-bottom-1.5"}>
                    <GithubIcon/>
                    GitHub
                </Button>
            </Link>
        )}
    </div>
}